import { FrequencyCap } from 'homepage-web-bff/types/TMpCard';

interface ViewData {
  count: number;
  timestamp: number;
}

const TIME_UNIT_MS = {
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
};

const setCookie = (name: string, value: string, expirationMs: number): void => {
  if (typeof document === 'undefined') return;

  const expires = new Date(Date.now() + expirationMs);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const getViewData = (bannerId: string): ViewData | null => {
  const cookieName = `mp_card_views_${bannerId}`;
  const data = getCookie(cookieName);

  if (!data) return null;

  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

const setViewData = (bannerId: string, viewData: ViewData, expirationMs: number): void => {
  const cookieName = `mp_card_views_${bannerId}`;
  setCookie(cookieName, JSON.stringify(viewData), expirationMs);
};

const shouldResetViewCount = (timestamp: number, timeUnit: FrequencyCap['timeUnit']): boolean => {
  const timeUnitMs = TIME_UNIT_MS[timeUnit];
  const now = Date.now();
  return now - timestamp >= timeUnitMs;
};

export const checkAndIncrementFrequencyCap = (bannerId: string, frequencyCap: FrequencyCap): boolean => {
  if (!frequencyCap?.maxViews || !frequencyCap?.timeUnit) {
    return true; // No frequency cap defined, allow display
  }

  const timeUnitMs = TIME_UNIT_MS[frequencyCap.timeUnit];
  if (!timeUnitMs) {
    return true;
  }

  const now = Date.now();
  let viewData = getViewData(bannerId);

  // Initialize or reset view data if time window has passed
  if (!viewData || shouldResetViewCount(viewData.timestamp, frequencyCap.timeUnit)) {
    viewData = { count: 0, timestamp: now };
  }

  // Check if max views reached
  if (viewData.count >= frequencyCap.maxViews) {
    return false; // Don't display banner
  }

  // Increment view count and update cookie
  viewData.count += 1;
  setViewData(bannerId, viewData, timeUnitMs);

  return true; // Display banner
};
